import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import ContactForm from "./components/ContactForm";

const principles = [
  {
    icon: "ri-scales-3-line",
    title: "只对比，不站队",
    desc: "我们呈现各方说了什么，不对当事人下道德判断，结论留给读者。",
  },
  {
    icon: "ri-links-line",
    title: "每条说法标来源",
    desc: "公开表述、截图、声明都会标注来源数量与信息完整度。",
  },
  {
    icon: "ri-history-line",
    title: "旧说法永不删除",
    desc: "新回应与旧说法并存，方便对照同一方前后是否自相矛盾。",
  },
  {
    icon: "ri-alarm-warning-line",
    title: "存疑就标存疑",
    desc: "证据不足的内容不会写成定论，避免二次传播未经核实的信息。",
  },
];

const steps = [
  {
    step: "01",
    title: "归档原始表述",
    desc: "收集各方公开的原话、声明与截图，保留时间点，不做删改润色。",
  },
  {
    step: "02",
    title: "拆出对比维度",
    desc: "按立场、是否正面回应核心问题、信息完整度三个维度并排整理。",
  },
  {
    step: "03",
    title: "标出冲突区间",
    desc: "把各方自述时间线叠在一起，重叠与矛盾处直接标出来。",
  },
  {
    step: "04",
    title: "持续追加进展",
    desc: "事件有新说法时按时间补充，旧内容保留，形成可回溯的记录。",
  },
];

const voices = [
  {
    name: "内容行业观察者 · 老彭",
    text: "这类站点的价值不在爆料速度，而在把口径对齐。信息一旦并排，含糊的地方藏不住。",
  },
  {
    name: "普通读者 · 小满",
    text: "以前刷到一条就信一条，现在会先来这里看看各方是不是说法一致，心态稳多了。",
  },
  {
    name: "品牌公关从业者 · 阿哲",
    text: "做危机沟通时回头看这类归档很有用，能看出哪一类表述最容易引发二次质疑。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 吃瓜观测站 · 网红大瓜多方说法归档",
    description:
      "吃瓜观测站的品牌定位、内容原则与判断标准。我们只做公开信息的整理与并列对比，不替任何一方下结论，并开放线索投稿与联系渠道。",
    keywords: "吃瓜观测站,关于我们,网红大瓜,多方说法对比,网红大瓜当事人回应",
    canonical: "https://ytrongxiang.cn/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜观测站",
        inLanguage: "zh-CN",
        url: "https://ytrongxiang.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "吃瓜观测站",
        url: "https://ytrongxiang.cn/",
        description: "网红大瓜多方说法与当事人回应的对比聚合站",
        email: "tip@chigua-guancha.example.com",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "线索投稿与联系",
        inLanguage: "zh-CN",
        url: "https://ytrongxiang.cn/about#contact",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ytrongxiang.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "关于我们",
            item: "https://ytrongxiang.cn/about",
          },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-300 bg-accent-50 px-3 py-1 font-label text-xs font-medium text-accent-900">
                <i className="ri-award-line"></i>
                关于吃瓜观测站
              </span>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                把散落各处的说法，
                <br className="hidden sm:block" />
                重新摆到同一张桌子上
              </h1>
              <p className="mt-4 font-label text-sm leading-relaxed text-foreground-600 md:text-base">
                热点事件里，爆料、回应、澄清往往散在不同平台、不同时间。吃瓜观测站做的事很朴素：把它们收集起来、对齐时间、并列呈现，让判断这件事重新回到读者手上。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-80 lg:h-[400px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20balanced%20scales%20formed%20by%20speech%20bubble%20shapes%20in%20coral%20red%2C%20amber%20gold%20and%20warm%20cream%20beige%2C%20minimalist%20magazine%20style%2C%20clean%20geometric%20composition%20on%20warm%20neutral%20background%2C%20high%20detail&width=1200&height=1200&seq=gossip-about-hero&orientation=squarish"
                  alt="吃瓜观测站 对比与平衡理念示意"
                  title="关于吃瓜观测站 - 多方说法平衡对比"
                  className="h-full w-full object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们是谁，为什么做这个站
              </h2>
              <div className="mt-4 space-y-4 font-label text-sm leading-loose text-foreground-600 md:text-base">
                <p>
                  我们是一群常年泡在信息流里的人。一次又一次看到，同一起事件里最先传开的那句话，往往不是最完整的版本；等到当事人回应出来，舆论早就跑出去好几公里。
                </p>
                <p>
                  所以吃瓜观测站选择不做「最快」，去做「最齐」。一个事件页里，你会看到爆料方、当事人、平台方、旁观者的说法并列摆开，谁在回避核心问题，谁前后口径不一致，会比较直观地显露出来。
                </p>
                <p>
                  我们不生产故事，也不替任何一方定性。我们只是把公开信息归档、对齐、并排，让你花三分钟就能建立自己的判断。
                </p>
              </div>

              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="font-heading text-2xl font-bold text-primary-600">128</span>
                  <p className="mt-1 font-label text-xs text-foreground-500">已归档事件</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="font-heading text-2xl font-bold text-accent-700">640+</span>
                  <p className="mt-1 font-label text-xs text-foreground-500">对比说法条目</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="font-heading text-2xl font-bold text-secondary-600">6</span>
                  <p className="mt-1 font-label text-xs text-foreground-500">事件分类</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2 font-label text-xs font-medium tracking-[0.2em] text-primary-600">
                <i className="ri-shield-check-line"></i>
                内容原则
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                四条不打折扣的规矩
              </h2>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {principles.map((p, index) => (
                <article
                  key={p.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                      <span className="font-label text-xs text-foreground-400">
                        0{index + 1}
                      </span>
                      {p.title}
                    </h3>
                    <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">
                      {p.desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2 font-label text-xs font-medium tracking-[0.2em] text-secondary-600">
                <i className="ri-route-line"></i>
                判断标准
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一个事件页是怎么被做出来的
              </h2>
              <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
                从收到线索到发布归档，中间会走完这四步，每一步都尽量留下可回溯的痕迹。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s) => (
                <article
                  key={s.step}
                  className="relative rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="font-heading text-3xl font-black text-background-300">
                    {s.step}
                  </span>
                  <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950">
                    {s.title}
                  </h3>
                  <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">
                    {s.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full border-t border-background-200 bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              读者与同行的反馈
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((v) => (
                <figure
                  key={v.name}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                  <blockquote className="mt-3 flex-1 font-label text-sm leading-relaxed text-foreground-700">
                    {v.text}
                  </blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-4 font-label text-xs font-medium text-foreground-900">
                    {v.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系方式
              </h2>
              <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
                除了右边的表单，你也可以直接发邮件给我们。涉及投稿的截图，建议同时提供原始链接，便于我们核对时间点。
              </p>

              <ul className="mt-6 space-y-4">
                <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-send-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block font-label text-xs text-foreground-500">投稿邮箱</span>
                    <a
                      href="mailto:tip@chigua-guancha.example.com"
                      className="mt-0.5 block font-label text-sm font-medium text-foreground-950 transition-colors hover:text-primary-700"
                    >
                      tip@chigua-guancha.example.com
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block font-label text-xs text-foreground-500">核实与回复</span>
                    <span className="mt-0.5 block font-label text-sm font-medium text-foreground-950">
                      通常在 3 个工作日内回复
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-forbid-2-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block font-label text-xs text-foreground-500">我们不接受</span>
                    <span className="mt-0.5 block font-label text-sm font-medium text-foreground-950">
                      有偿删帖与未经核实的匿名指控
                    </span>
                  </span>
                </li>
              </ul>

              <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <p className="font-label text-sm leading-relaxed text-foreground-700">
                  还不知道从哪看起？先从热度最高的几个事件开始，对照着看一遍，就明白「多方说法对比」是怎么用的了。
                </p>
                <Link
                  to="/list"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-fire-line"></i>
                  去大瓜合集
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}